import type {AdbContextServiceV2} from '@smart/adb-context-service';
import type {LoginServiceV3} from '@smart/login-service';
import type {
  PricePreferencesProviderV1,
  PriceTypeV1,
} from '@smart/price-preferences-provider';
import * as React from 'react';

type PriceTypeHookProps = {
  adbContextService: AdbContextServiceV2;
  loginService: LoginServiceV3;
  pricePreferencesProvider: PricePreferencesProviderV1;
};

export function usePriceType({
  adbContextService,
  loginService,
  pricePreferencesProvider,
}: PriceTypeHookProps) {
  const isLoggedIn = loginService.loginState.status === `logged-in`;
  const isADBAgent = Boolean(
    isLoggedIn &&
      adbContextService.adbContext?.hasCustomer &&
      adbContextService.adbContext?.customerType,
  );
  const customerType = isADBAgent
    ? (adbContextService.adbContext?.customerType as PriceTypeV1)
    : isLoggedIn
    ? loginService.loginState.customerType
    : pricePreferencesProvider.getPriceType();

  const [priceType, setPriceType] = React.useState(customerType);

  React.useEffect(() => {
    // we should only subscribe to pricePreferencesProvider if user is not logged in
    if (!isLoggedIn) {
      const unsubscribe = pricePreferencesProvider.subscribe(() =>
        setPriceType(pricePreferencesProvider.getPriceType()),
      );

      return () => {
        unsubscribe();
      };
    }

    return () => {};
  }, [pricePreferencesProvider, loginService, isLoggedIn]);

  return [priceType, pricePreferencesProvider.setPriceType] as const;
}
